/**
 * V165.3 — Anti-RankPill Power Trial Offer Modal
 *
 * Intercepts churn intent (cancel click, downgrade, etc.) and offers a free
 * 3-day Power Trial of the highest tier before the user reaches the cancel
 * flow. Backend manages cooldown (90 days) and tier unlock automatically.
 *
 * Usage:
 *   <PowerTrialOfferModal
 *     open={showModal}
 *     source="cancel_click"
 *     onAccepted={({trial_ends_at}) => {...}}
 *     onDeclined={() => proceedToCancel()}
 *     onClose={() => setShowModal(false)}
 *   />
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../config";
import { useLanguage } from "../../contexts/LanguageContext";
import {
  X, Sparkles, Zap, Loader2, ShieldCheck, Clock, Gift, AlertCircle,
} from "lucide-react";

export default function PowerTrialOfferModal({
  open, source, planId, onAccepted, onDeclined, onClose,
}) {
  const { t } = useLanguage();
  const [phase, setPhase] = useState("checking"); // checking | offered | accepting | active | unavailable | error
  const [offer, setOffer] = useState(null);
  const [reason, setReason] = useState("");

  // Step 1 — ask backend whether we can offer (cooldown check)
  useEffect(() => {
    if (!open) return;
    setPhase("checking");
    setOffer(null);
    setReason("");
    (async () => {
      try {
        const r = await axios.post(
          `${API}/anti-churn/intent`,
          { source: source || "cancel_click", plan_id: planId || null },
          { withCredentials: true }
        );
        if (r.data?.can_redeem && r.data?.offer) {
          setOffer(r.data.offer);
          setPhase("offered");
        } else {
          setReason(r.data?.reason || "no_offer");
          setPhase("unavailable");
        }
      } catch (e) {
        setReason(e?.response?.data?.detail || "intent_failed");
        setPhase("error");
      }
    })();
  }, [open, source, planId]);

  const accept = async () => {
    setPhase("accepting");
    try {
      const r = await axios.post(
        `${API}/anti-churn/redeem`,
        { accept: true },
        { withCredentials: true }
      );
      if (r.data?.ok && r.data?.status === "active") {
        setPhase("active");
        onAccepted?.({
          tier: r.data.tier,
          trial_ends_at: r.data.trial_ends_at,
          days: r.data.days,
        });
      } else {
        setPhase("error");
      }
    } catch {
      setPhase("error");
    }
  };

  const decline = async () => {
    try {
      await axios.post(
        `${API}/anti-churn/redeem`,
        { accept: false },
        { withCredentials: true }
      );
    } catch { /* still proceed */ }
    onDeclined?.();
    onClose?.();
  };

  // Auto-close on unavailable → proceed to cancel
  useEffect(() => {
    if (phase === "unavailable") {
      const timeout = setTimeout(() => {
        onDeclined?.();
        onClose?.();
      }, 200);
      return () => clearTimeout(timeout);
    }
  }, [phase, onDeclined, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={() => phase !== "accepting" && onClose?.()}
      data-testid="power-trial-modal"
    >
      <div
        className="bg-white rounded-xl max-w-md w-full overflow-hidden shadow-2xl relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header gradient */}
        <div className="bg-gradient-to-br from-purple-600 via-fuchsia-600 to-amber-500 p-6 text-white relative">
          <button
            onClick={onClose}
            disabled={phase === "accepting"}
            className="absolute top-3 right-3 text-white/70 hover:text-white"
            data-testid="power-trial-close"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2 mb-3">
            <Gift className="w-5 h-5" />
            <span className="text-xs font-black uppercase tracking-[0.2em]">
              {t.ptr_eyebrow || "Špeciálna ponuka iba pre teba"}
            </span>
          </div>
          <h2 className="text-2xl font-black leading-tight font-['Chivo']" data-testid="power-trial-title">
            {phase === "active"
              ? (t.ptr_active_h || "Power Trial je aktívny 🎉")
              : (t.ptr_title || "Pred odchodom — 3 dni Power zdarma")}
          </h2>
          <p className="text-sm text-white/90 mt-2">
            {phase === "active"
              ? (t.ptr_active_sub || "Tvoja Power úroveň je odomknutá. Užívaj.")
              : (t.ptr_sub || "Odomkneme ti najvyššiu úroveň na 3 dni — bez záväzku. Po skončení sa nič nestrhne.")}
          </p>
        </div>

        {/* Body */}
        <div className="p-6">
          {phase === "checking" && (
            <div className="flex items-center gap-3 text-gray-500 py-6 justify-center">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm">{t.ptr_checking || "Pripravujem tvoju ponuku…"}</span>
            </div>
          )}

          {phase === "offered" && (
            <>
              <ul className="space-y-3 mb-6" data-testid="power-trial-perks">
                <Perk icon={Zap} text={t.ptr_perk1 || "Všetky AI agenty plne odomknuté"} />
                <Perk icon={ShieldCheck} text={t.ptr_perk2 || "Prioritný support a unlimited skeny"} />
                <Perk icon={Clock} text={t.ptr_perk3 || "Trvanie 3 dni · automaticky končí · žiadny poplatok"} />
                <Perk icon={Sparkles} text={t.ptr_perk4 || "Plný prístup k Ad Studio + 1670 šablónam"} />
              </ul>
              <button
                onClick={accept}
                className="w-full bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-700 hover:to-fuchsia-700 text-white font-black text-base py-3.5 rounded-lg uppercase tracking-wider transition-all shadow-lg"
                data-testid="power-trial-accept"
              >
                {t.ptr_accept || "Aktivovať 3 dni zdarma"}
              </button>
              <button
                onClick={decline}
                className="w-full mt-2 text-gray-500 hover:text-gray-700 text-sm py-2 underline-offset-2 hover:underline"
                data-testid="power-trial-decline"
              >
                {t.ptr_decline || "Nie, vďaka — pokračovať v zrušení"}
              </button>
            </>
          )}

          {phase === "accepting" && (
            <div className="flex items-center gap-3 text-gray-700 py-6 justify-center">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm">{t.ptr_activating || "Aktivujem Power Trial…"}</span>
            </div>
          )}

          {phase === "active" && (
            <div className="text-center py-4">
              <p className="text-gray-700 mb-4">{t.ptr_active_done || "Tvoja Power úroveň je odomknutá na 3 dni."}</p>
              <button
                onClick={onClose}
                className="bg-purple-600 hover:bg-purple-700 text-white font-bold px-8 py-3 rounded-lg"
                data-testid="power-trial-done"
              >
                {t.ptr_continue || "Pokračovať"}
              </button>
            </div>
          )}

          {phase === "error" && (
            <div className="flex items-start gap-3 text-red-600 bg-red-50 p-4 rounded">
              <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-bold">{t.ptr_err || "Nepodarilo sa aktivovať ponuku"}</p>
                <p className="text-xs mt-1 text-red-500">{reason || ""}</p>
              </div>
            </div>
          )}
        </div>

        {phase === "offered" && (
          <p className="text-[10px] text-gray-400 text-center pb-3 px-6">
            {t.ptr_footer || "Bez kreditnej karty · Automaticky končí o 3 dni · 1× za 90 dní"}
          </p>
        )}
      </div>
    </div>
  );
}

function Perk({ icon: Icon, text }) {
  return (
    <li className="flex items-start gap-3 text-sm">
      <Icon className="w-4 h-4 text-purple-600 shrink-0 mt-0.5" />
      <span className="text-gray-700">{text}</span>
    </li>
  );
}
